import { type FC, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageTitle } from "../../../../components/PageTitle";
import {
  useContactApi,
  useDeleteContactApi,
} from "../../../../hooks/use-distribution-api";

export const ContactDetailPage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const {
    data: contact,
    loading,
    error,
  } = useContactApi({
    id: id || "",
    skip: !id,
  });
  const { deleteContact } = useDeleteContactApi();

  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const handleDelete = async () => {
    if (!id) return;
    setActionLoading(true);
    try {
      await deleteContact(id);
      navigate("/admin/distribution/contacts");
    } catch (e) {
      alert(`エラー: ${e instanceof Error ? e.message : "不明なエラー"}`);
    } finally {
      setActionLoading(false);
    }
  };

  if (error) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先詳細</PageTitle>
        <div className="alert alert-error">
          <span>エラーが発生しました: {error.message}</span>
        </div>
        <Link to="/admin/distribution/contacts" className="btn btn-ghost mt-4">
          ← 一覧に戻る
        </Link>
      </div>
    );
  }

  if (loading || !contact) {
    return (
      <div className="container mx-auto px-4 py-6">
        <PageTitle>連絡先詳細</PageTitle>
        <div className="flex justify-center py-8">
          <span className="loading loading-spinner loading-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <Link
            to="/admin/distribution/contacts"
            className="btn btn-ghost btn-sm"
          >
            ← 戻る
          </Link>
          <PageTitle>{contact.contactName || contact.companyName}</PageTitle>
          <span
            className={`badge ${
              contact.isActive ? "badge-success" : "badge-error"
            }`}
          >
            {contact.isActive ? "有効" : "無効"}
          </span>
        </div>
        <div className="flex gap-2">
          <Link
            to={`/admin/distribution/contacts/${id}/edit`}
            className="btn btn-primary btn-sm"
          >
            編集
          </Link>
          <button
            type="button"
            onClick={() => setDeleteConfirm(true)}
            className="btn btn-error btn-sm"
          >
            削除
          </button>
        </div>
      </div>

      {/* 基本情報 */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-lg">基本情報</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <div>
              <div className="text-sm text-base-content/60">会社名</div>
              <div className="font-medium">{contact.companyName}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">担当者名</div>
              <div>{contact.contactName || "-"}</div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">メールアドレス</div>
              <div>
                <a
                  href={`mailto:${contact.email}`}
                  className="link link-primary"
                >
                  {contact.email}
                </a>
              </div>
            </div>
            <div>
              <div className="text-sm text-base-content/60">管理者</div>
              <div>{contact.managedBy?.name || "-"}</div>
            </div>
            <div className="md:col-span-2">
              <div className="text-sm text-base-content/60">備考</div>
              <div>{contact.notes || "-"}</div>
            </div>
          </div>
        </div>
      </div>

      {/* 所属グループ */}
      <div className="card bg-base-100 shadow-md mb-6">
        <div className="card-body">
          <h2 className="card-title text-lg">
            所属グループ ({contact.groups.length}件)
          </h2>
          {contact.groups.length === 0 ? (
            <div className="text-center py-4 text-base-content/60">
              グループに所属していません
            </div>
          ) : (
            <div className="flex flex-wrap gap-2 mt-4">
              {contact.groups.map(group => (
                <Link
                  key={group.id}
                  to={`/admin/distribution/groups/${group.id}`}
                  className={`badge badge-lg gap-2 ${
                    group.isDefault ? "badge-warning" : "badge-ghost"
                  }`}
                >
                  {group.isDefault && "★ "}
                  {group.name}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* 配信履歴 */}
      <div className="card bg-base-100 shadow-md">
        <div className="card-body">
          <h2 className="card-title text-lg">
            配信履歴 ({contact.deliveryHistory.length}件)
          </h2>
          {contact.deliveryHistory.length === 0 ? (
            <div className="text-center py-4 text-base-content/60">
              配信履歴がありません
            </div>
          ) : (
            <div className="overflow-x-auto mt-4">
              <table className="table">
                <thead>
                  <tr>
                    <th>日時</th>
                    <th>件名</th>
                    <th>ステータス</th>
                  </tr>
                </thead>
                <tbody>
                  {contact.deliveryHistory.map(history => (
                    <tr key={history.id} className="hover">
                      <td>
                        {history.sentAt
                          ? new Date(history.sentAt).toLocaleString("ja-JP")
                          : "-"}
                      </td>
                      <td>{history.subject}</td>
                      <td>
                        <span className="badge badge-sm badge-ghost">
                          {history.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* 削除確認モーダル */}
      {deleteConfirm && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg">連絡先を削除しますか？</h3>
            <p className="py-4">この操作は取り消せません。</p>
            <div className="modal-action">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={() => setDeleteConfirm(false)}
                disabled={actionLoading}
              >
                キャンセル
              </button>
              <button
                type="button"
                className="btn btn-error"
                onClick={handleDelete}
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <span className="loading loading-spinner loading-sm" />
                ) : (
                  "削除"
                )}
              </button>
            </div>
          </div>
          <div
            className="modal-backdrop"
            onClick={() => !actionLoading && setDeleteConfirm(false)}
          />
        </div>
      )}
    </div>
  );
};
